import React from "react";
import {
  LayoutDashboard,
  MessageCircle,
  Settings as SettingsIcon,
  User,
} from "lucide-react";
import { useUnreadLeads } from "../../hooks/UnreadLeadsContext";

/**
 * MobileBottomNav
 *
 * iOS/Android-native style bottom tab bar shown only on mobile breakpoints
 * (CSS hides this on desktop via the .mobile-bottom-nav rules in styles.css).
 *
 * Two role variants:
 *   - tradie:   Dashboard · Quotes · Pipeline · Settings
 *   - customer: Dashboard · Messages · Settings
 *
 * Pairs with the slim top action bar in Header.js — together they form the
 * "hybrid" mobile nav pattern (top = brand + contextual action; bottom = nav).
 */
export default function MobileBottomNav({ role, currentView, setCurrentView }) {
  const { unreadCount } = useUnreadLeads();

  if (!role) return null;

  // Top-level views only. Sub-views (TradieDashboard internal tabs like
  // Today/Pipeline/Quotes) will get their own deep-link handler in a follow-up
  // session — for now the Home tab routes back to the dashboard root and the
  // user uses the dashboard's existing internal tabs.
  const tabs =
    role === "tradie"
      ? [
          {
            id: "tradie",
            label: "Home",
            icon: LayoutDashboard,
            views: ["tradie"],
            badge: unreadCount,
          },
          {
            id: "settings",
            label: "Settings",
            icon: SettingsIcon,
            views: ["settings"],
          },
        ]
      : [
          {
            id: "customer",
            label: "Home",
            icon: LayoutDashboard,
            views: ["customer"],
          },
          {
            id: "customer-messages",
            label: "Messages",
            icon: MessageCircle,
            views: ["customer-messages"],
            badge: unreadCount,
          },
          {
            id: "customer-settings",
            label: "Settings",
            icon: User,
            views: ["customer-settings"],
          },
        ];

  return (
    <nav
      className="mobile-bottom-nav"
      role="navigation"
      aria-label="Primary navigation"
    >
      {tabs.map((tab) => {
        const Icon = tab.icon;
        const isActive = tab.views.includes(currentView);
        return (
          <button
            key={tab.id}
            type="button"
            className={`mobile-bottom-nav__tab${
              isActive ? " mobile-bottom-nav__tab--active" : ""
            }`}
            onClick={() => setCurrentView(tab.id)}
            aria-label={tab.label}
            aria-current={isActive ? "page" : undefined}
          >
            <span className="mobile-bottom-nav__icon-wrap">
              <Icon size={22} strokeWidth={isActive ? 2.4 : 2} />
              {tab.badge > 0 && (
                <span className="mobile-bottom-nav__badge" aria-hidden="true">
                  {tab.badge > 9 ? "9+" : tab.badge}
                </span>
              )}
            </span>
            <span className="mobile-bottom-nav__label">{tab.label}</span>
          </button>
        );
      })}
    </nav>
  );
}
